import React from 'react'
import { css } from '@emotion/react'
import omit from 'lodash/omit'

import Colors from '../theme/Colors'
import ClickArea from './ClickArea'
import Link from './Link'
import LoadingBubbles from './LoadingBubbles'

export default function Button(
  props: React.ButtonHTMLAttributes<HTMLElement> &
    React.ComponentProps<typeof ClickArea> & {
      size?: 'large' | 'stretch'
      color?: 'teal' | 'tealOutline' | 'orange' | 'red' | 'white' | 'gray'
      loading?: boolean
    },
) {
  const passThroughProps = omit(props, 'size', 'color', 'loading')

  return (
    <ClickArea
      {...passThroughProps}
      css={[
        css`
          ${normalButtonStyle};
          border-radius: 2px;
          border: none;
          font-weight: 600;
          user-select: none;
          transition: background-color 0.3s;

          &:disabled {
            background: ${Colors.Gray100};
            color: ${Colors.Gray300};
          }
        `,
        props.size === 'large' && largeButtonStyle,
        props.size === 'stretch' && stretchButtonStyle,
        (props.color === 'teal' || !props.color) && tealButtonStyle,
        props.color === 'tealOutline' && tealOutlineButtonStyle,
        props.color === 'orange' && orangeButtonStyle,
        props.color === 'red' && redButtonStyle,
        props.color === 'white' && whiteButtonStyle,
        props.color === 'gray' && grayButtonStyle,
      ]}
      disabled={props.disabled || props.loading}
      role="button"
    >
      {props.loading ? <LoadingBubbles size={14} color={Colors.Gray400} /> : props.children}
    </ClickArea>
  )
}

export const normalButtonStyle = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 5px 15px;
  min-width: 65px;
  min-height: 34px;
  box-sizing: border-box;
`

export const largeButtonStyle = css`
  ${normalButtonStyle}
  padding: 5px 30px;
  min-width: 150px;
  min-height: 42px;
`

export const stretchButtonStyle = css`
  ${largeButtonStyle}
  display: flex;
  width: 100%;
  min-width: auto;
`

const tealButtonStyle = css`
  background: ${Colors.Teal400};
  color: white;

  :not(:disabled) {
    &:hover {
      background: ${Colors.Teal500};
    }

    &:active {
      background: ${Colors.Teal600};
    }
  }
`

const tealOutlineButtonStyle = css`
  border: 1px solid currentColor;
  color: ${Colors.Teal400};

  :not(:disabled) {
    &:hover {
      color: ${Colors.Teal500};
    }

    &:active {
      color: ${Colors.Teal600};
    }
  }
`

const orangeButtonStyle = css`
  background: ${Colors.Amber400};
  color: white;

  :not(:disabled) {
    &:hover {
      background: ${Colors.Amber500};
    }

    &:active {
      background: ${Colors.Amber600};
    }
  }
`

const redButtonStyle = css`
  background: ${Colors.Red400};
  color: white;

  :not(:disabled) {
    &:hover {
      background: ${Colors.Red500};
    }

    &:active {
      background: ${Colors.Red600};
    }
  }
`

const whiteButtonStyle = css`
  background: ${Colors.White};
  color: ${Colors.Gray600};
  border: 1px solid ${Colors.Gray200};

  :not(:disabled) {
    &:hover {
      background: ${Colors.Gray100};
    }

    &:active {
      background: ${Colors.Gray200};
    }
  }
`

const grayButtonStyle = css`
  background: ${Colors.Gray50};
  color: ${Colors.Gray600};

  :not(:disabled) {
    &:hover {
      background: ${Colors.Gray200};
    }

    &:active {
      background: ${Colors.Gray300};
    }
  }
`

function getChildren(children: React.ReactNode): Array<React.ReactNode> {
  if (Array.isArray(children)) {
    return children
  }

  if (React.isValidElement(children) && children.type === React.Fragment) {
    return children.props.children as Array<React.ReactNode>
  }

  if (!children) {
    return []
  }

  return [children]
}

export function mapChildren(
  children: React.ReactNode,
  iterator: (element: React.ReactElement, index: number) => React.ReactElement,
) {
  return React.Children.map(getChildren(children), (node, index) => {
    if (React.isValidElement(node)) {
      return iterator(node, index)
    }
    return null
  })
}

export function enlargeButtonLike(
  node: React.ReactElement,
  /**
   * undefined represents default Button/Link size
   */
  size: 'large' | 'stretch' | undefined,
): React.ReactElement {
  if (node.type === Button) {
    return React.cloneElement<React.ComponentProps<typeof Button>>(node, { size })
  }

  if (node.type === Link) {
    return React.cloneElement<React.ComponentProps<typeof Link>>(node, { size })
  }

  return node
}
